<template>
  <div>
    <v-row>
      <!-- DKIM -->
      <v-col cols="12">
        <gg-card>
          <gg-card-header>
            DKIM - {{ domain }}
          </gg-card-header>
          <gg-card-text>
            <v-row v-if="pendingDetails">
              <v-col  cols="12" class="d-flex justify-center align-items-center pa-10">
                <v-progress-circular indeterminate color="primary" size="80" />
              </v-col>
            </v-row>
            <v-row v-else-if="!hasResult">
              <v-col cols="12">
                <v-alert type="error" text dense class="mb-0">
                  We couldn't load the DKIM details for {{ domain }}.
                  <gg-btn small tertiary class="ml-2" @click="loadDetails">Try again</gg-btn>
                </v-alert>
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col cols="12">
                <v-alert type="info" dense v-if="dkimData.state === 'MISSING'">
                  A “DKIM” record does <b>not</b> exist for this domain. To properly configure your DKIM key, the record must use this server’s DKIM key.
                </v-alert>
                <v-alert type="error" dense v-if="Boolean(!nsData.local_authority)">
                  This system does not control DNS for the "{{ domain }}" domain. You can install the suggested “DKIM”
                  record locally. However, this server is not the authoritative nameserver. If you install this record,
                  this change will not be effective. Contact the person responsible for the nameservers and request that
                  they update the “DKIM” record with the following:
                </v-alert>
              </v-col>
              <v-col cols="12" class="font-weight-bold">
                Suggested “DKIM” (TXT) Record
              </v-col>
              <v-col cols="12" md="6">
                <b>Name</b>
                <p>{{ dkimData.domain }}</p>
<!--                <gg-text-field label="Name" :value="dkimData.domain" readonly />-->
              </v-col>
              <v-col cols="12" md="6">
                <b>Value</b>
                <p>{{ dkimData.expected }}</p>
<!--                <gg-text-field label="Value" :value="dkimData.expected" readonly />-->
              </v-col>
              <v-col cols="12">
<!--                <gg-btn primary :loading="pendingDkim" @click="setDkim">-->
<!--                  Install the Suggested Record-->
<!--                </gg-btn>-->
              </v-col>
            </v-row>
          </gg-card-text>
        </gg-card>
      </v-col>
      <!-- SPF -->
      <v-col cols="12">
        <gg-card>
          <gg-card-header>
            SPF - {{ domain }}
          </gg-card-header>
          <gg-card-text>
            <v-row v-if="pendingDetails">
              <v-col cols="12" class="d-flex justify-center align-items-center pa-10">
                <v-progress-circular indeterminate color="primary" size="80" />
              </v-col>
            </v-row>
            <v-row v-else-if="!hasResult">
              <v-col cols="12">
                <v-alert type="error" text dense class="mb-0">
                  We couldn't load the SPF details for {{ domain }}.
                  <gg-btn small tertiary class="ml-2" @click="loadDetails">Try again</gg-btn>
                </v-alert>
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col cols="12" v-if="spfData.state !== 'VALID'">
                <v-alert type="info" dense>
                  “SPF” is not properly configured for this domain.
                </v-alert>
              </v-col>
              <v-col cols="12" class="py-0"  v-if="spfData.state !== 'VALID'">
                To properly configure your SPF record, you must include the following mechanisms:
                <code>{{ spfData.expected }}</code>
              </v-col>
              <v-col cols="12" v-if="Boolean(!nsData.local_authority)">
                <v-alert type="error" dense>
                  This system does not control DNS for the "{{ domain }}" domain and the system did not find any
                  authoritative nameservers for this domain. You can install the suggested “SPF” record locally.
                  However, this server is not the authoritative nameserver. If you install this record, this change will
                  not be effective. Contact your domain registrar to verify this domain’s registration.
                </v-alert>
              </v-col>
              <v-col cols="12" class="font-weight-bold">
                Suggested “SPF” (TXT) Record
              </v-col>
              <v-col cols="12" md="6">
                <b>Name</b>
                <p>{{ spfData.domain }}</p>
<!--                <gg-text-field label="Name" :value="spfData.domain" readonly />-->
              </v-col>
              <v-col cols="12" md="6">
                <b>Value</b>
                <p>{{ spfExpected }}</p>
<!--                <gg-text-field label="Value" :value="spfExpected" readonly />-->
              </v-col>
              <v-col cols="12">
<!--                <gg-btn primary :loading="pendingSpf" @click="setSpf">-->
<!--                  Install the Suggested Record-->
<!--                </gg-btn>-->
              </v-col>
            </v-row>
          </gg-card-text>
        </gg-card>
      </v-col>
<!--      <v-col cols="12">-->
<!--        <gg-card>-->
<!--          <gg-card-header>-->
<!--            REVERSE DNS (PTR) - {{ domain }}-->
<!--          </gg-card-header>-->
<!--          <gg-card-text>-->
<!--            <div v-if="pendingDetails" class="d-flex justify-center align-items-center pa-10">-->
<!--              <v-progress-circular indeterminate color="primary" size="80" />-->
<!--            </div>-->
<!--          </gg-card-text>-->
<!--        </gg-card>-->
<!--      </v-col>-->
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from 'vuex'

export default {
  name: 'ManageAuthenticationView',
  props: {
    // The domain to show. Read from the route's `:domain` when not given.
    forDomain: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    pendingDetails: true,
    pendingDkim: false,
    pendingSpf: false
  }),
  computed: {
    ...mapGetters({
      domain: 'email_deliverability/selectedDomain',
      queryResult: 'email_deliverability/queryResult'
    }),
    // `queryResult` stays empty when the batch fails, so every read is
    // guarded; `hasResult` decides whether the records render at all.
    nsData () {
      return this.batchRow(0)
    },
    dkimData () {
      return this.batchRow(1)
    },
    spfData () {
      return this.batchRow(2)
    },
    hasResult () {
      return !!(this.nsData && this.dkimData && this.spfData)
    },
    spfExpected () {
      return this.spfData ? `v=spf1 +${this.spfData.expected} -all` : ''
    }
  },
  created () {
    const domain = this.forDomain || this.$route.params.domain
    this.setDomain(domain)
  },
  async mounted () {
    await this.loadDetails()
  },
  methods: {
    ...mapActions({
      batchQuery: 'email_deliverability/batchQuery',
      setDkimRecord: 'email_deliverability/setDkimRecord',
      setSpfRecord: 'email_deliverability/setSpfRecord',
      reportError: 'reportError',
    }),
    ...mapMutations({
      setDomain: 'email_deliverability/SET_DOMAIN',
    }),
    batchRow (index) {
      const result = (this.queryResult || [])[index]
      return (result && result.data && result.data[0]) || null
    },
    async loadDetails () {
      try {
        this.pendingDetails = true
        await this.batchQuery({ single: true, _username: this.$route.query.user })
      } catch (e) {
        console.log(e)
        this.reportError(e)
      } finally {
        this.pendingDetails = false
      }
    },
    async setDkim () {
      try {
        this.pendingDkim = true
        await this.setDkimRecord({
          domain: this.domain,
          record: this.dkimData.expected,
          _username: this.$route.query.user,
        })
        await this.loadDetails()
      } catch (e) {
        console.log(e)
      } finally {
        this.pendingDkim = false
      }
    },
    async setSpf () {
      try {
        this.pendingSpf = true
        await this.setSpfRecord({
          domain: this.domain,
          record: this.spfExpected,
          _username: this.$route.query.user,
        })
        await this.loadDetails()
      } catch (e) {
        console.log(e)
      } finally {
        this.pendingSpf = false
      }
    }
  }
}
</script>

<style scoped>

</style>
